/**
 * Human labels for a TasAsset's `classification` — the approval step a document
 * was uploaded for.
 *
 * Surfaces that list documents from more than one step side by side (the legacy
 * free-form attachment table, the evidence step's prior-uploads history) need
 * this: the classification is the only thing distinguishing an approval receipt
 * from a completion transcript in a flat list. Shared rather than inlined
 * per-component so the two lists can't drift apart — and so the labels are
 * translatable, which the original inline map was not.
 */
// A Map, not an object literal: `classification` arrives straight off a server
// record, and an object literal would resolve inherited keys ("constructor")
// to a truthy non-key.
const CLASSIFICATION_LABEL_KEYS = new Map<string, string>([
  ['COMPLETION_DOCUMENTS', 'tas.attachments.classifications.completion'],
  ['COURSE_APPROVAL_DOCUMENTS', 'tas.attachments.classifications.course_approval'],
  ['PROGRAM_APPROVAL_DOCUMENTS', 'tas.attachments.classifications.program_approval'],
  ['PROGRAM_AGREEMENT_DOCUMENTS', 'tas.attachments.classifications.program_agreement'],
  ['PROGRAM_COMPLETION_DOCUMENTS', 'tas.attachments.classifications.program_completion'],
  ['APPLICATION_LIFECYCLE_DOCUMENTS', 'tas.attachments.classifications.application_lifecycle'],
]);

/**
 * Translation key for a classification's label, or `null` when the
 * classification is absent or unrecognized — callers render nothing rather than
 * a raw enum, matching what the legacy attachment table did with its unmapped
 * values. Returning the key (not the string) keeps this usable from a
 * template-only component via `{{t}}`.
 */
export function classificationLabelKey(classification: string | null | undefined): string | null {
  if (!classification) return null;
  return CLASSIFICATION_LABEL_KEYS.get(classification) ?? null;
}
